/**
 * Currency Formatting Utilities
 *
 * Provides locale-aware currency formatting following best practices:
 * - Uses Intl.NumberFormat for proper localization
 * - Respects currency-specific decimal places (e.g., JPY has 0, KWD has 3)
 * - Uses appropriate thousand/decimal separators per locale
 *
 * Examples:
 * - USD 1234.56 -> $1,234.56
 * - EUR 1234.56 -> 1.234,56 € (German locale)
 * - JPY 1234    -> ¥1,234 (no decimals)
 * - INR 1234.56 -> ₹1,234.56 (Indian numbering: 1,23,456.78 for larger)
 */

import { getCurrency, DEFAULT_CURRENCY } from '../constants/currencies';

/**
 * Format a number as currency with proper localization
 *
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code (default: USD)
 * @param {Object} options - Additional formatting options
 * @param {boolean} options.showCode - Show currency code instead of symbol
 * @param {boolean} options.compact - Use compact notation for large numbers
 * @returns {string} Formatted currency string
 *
 * @example
 * formatCurrency(1234.56, 'USD')     // '$1,234.56'
 * formatCurrency(1234.56, 'EUR')     // '1.234,56 €'
 * formatCurrency(1234, 'JPY')        // '¥1,234'
 * formatCurrency(1234.567, 'KWD')    // 'KWD 1,234.567' (3 decimals)
 */
export function formatCurrency(amount, currencyCode = DEFAULT_CURRENCY, options = {}) {
  const { showCode = false, compact = false } = options;

  // Handle null/undefined amounts
  if (amount == null || isNaN(amount)) {
    amount = 0;
  }

  // Get currency info for locale
  const currency = getCurrency(currencyCode);
  const locale = currency?.locale || 'en-US';
  const code = currency?.code || currencyCode?.toUpperCase() || DEFAULT_CURRENCY;

  try {
    const formatOptions = {
      style: 'currency',
      currency: code,
      // Let Intl.NumberFormat handle decimals based on currency
      // It knows JPY = 0, USD = 2, KWD = 3, etc.
    };

    if (compact) {
      formatOptions.notation = 'compact';
      formatOptions.compactDisplay = 'short';
    }

    if (showCode) {
      formatOptions.currencyDisplay = 'code';
    }

    return new Intl.NumberFormat(locale, formatOptions).format(amount);
  } catch (error) {
    // Fallback for unknown currencies
    console.warn(`Currency formatting failed for ${code}:`, error);
    return `${code} ${amount.toFixed(2)}`;
  }
}

/**
 * Format currency for display in tables/lists (more compact)
 * Shows symbol only, no currency code
 *
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Compact formatted currency string
 */
export function formatCurrencyCompact(amount, currencyCode = DEFAULT_CURRENCY) {
  return formatCurrency(amount, currencyCode, { compact: false });
}

/**
 * Format currency with explicit code (useful when symbol is ambiguous)
 * e.g., $ could be USD, CAD, AUD, etc.
 *
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Currency string with code
 */
export function formatCurrencyWithCode(amount, currencyCode = DEFAULT_CURRENCY) {
  return formatCurrency(amount, currencyCode, { showCode: true });
}

/**
 * Parse a currency string back to a number
 * Handles various formats: $1,234.56, 1.234,56 €, ¥1,234
 *
 * @param {string} value - Currency string to parse
 * @param {string} currencyCode - Currency code for context
 * @returns {number} Parsed number value
 */
export function parseCurrencyString(value, currencyCode = DEFAULT_CURRENCY) {
  if (typeof value === 'number') return value;
  if (!value || typeof value !== 'string') return 0;

  const currency = getCurrency(currencyCode);

  // Remove currency symbols and codes
  let cleaned = value
    .replace(/[^\d.,\-]/g, '') // Keep only digits, dots, commas, minus
    .trim();

  if (!cleaned) return 0;

  // Determine decimal separator based on locale
  // European: 1.234,56 (comma is decimal)
  // US/UK: 1,234.56 (period is decimal)
  const locale = currency?.locale || 'en-US';
  const isEuropeanFormat = ['de-DE', 'de-CH', 'fr-FR', 'es-ES', 'it-IT', 'pt-BR', 'nl-NL', 'pl-PL', 'ru-RU', 'tr-TR'].includes(locale);

  if (isEuropeanFormat) {
    // European format: 1.234,56 -> 1234.56
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else {
    // US format: 1,234.56 -> 1234.56
    cleaned = cleaned.replace(/,/g, '');
  }

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Get the number of decimal places for a currency
 *
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {number} Number of decimal places
 */
export function getCurrencyDecimals(currencyCode) {
  const currency = getCurrency(currencyCode);
  return currency?.decimals ?? 2;
}

/**
 * Round amount to currency's decimal places
 *
 * @param {number} amount - Amount to round
 * @param {string} currencyCode - Currency code
 * @returns {number} Rounded amount
 */
export function roundToCurrencyPrecision(amount, currencyCode = DEFAULT_CURRENCY) {
  const decimals = getCurrencyDecimals(currencyCode);
  const factor = Math.pow(10, decimals);
  return Math.round(amount * factor) / factor;
}
